import { useEffect, useState } from 'react';
import { useCloseShellNav, useShellScrollArea } from '@/components/layout/shellNav';

export interface DashboardSection {
  id: string;
  label: string;
}

// A section is current once its top is this close to the top of the scrolling panel.
const ACTIVE_OFFSET_PX = 48;

/**
 * Links to each dashboard widget, shown in the sidebar under "Dashboard".
 * Highlights the widget currently at the top of the page.
 */
export function DashboardSectionLinks({ sections }: { sections: DashboardSection[] }) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const closeNav = useCloseShellNav();
  const scrollArea = useShellScrollArea();

  useEffect(() => {
    if (!scrollArea) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const top = scrollArea.getBoundingClientRect().top;
      let current: string | null = null;
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el && el.getBoundingClientRect().top - top <= ACTIVE_OFFSET_PX)
          current = section.id;
      }
      setActiveId(current ?? sections[0]?.id ?? null);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    scrollArea.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      scrollArea.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [scrollArea, sections]);

  if (sections.length < 2) return null;

  const jumpTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveId(id);
    closeNav();
  };

  return (
    <ul
      aria-label="Dashboard sections"
      className="mb-2 ml-[2.4rem] mr-4 mt-1 border-l border-gray-200 dark:border-gray-700"
    >
      {sections.map(({ id, label }) => {
        const active = id === activeId;
        return (
          <li key={id}>
            <button
              type="button"
              onClick={() => jumpTo(id)}
              aria-current={active ? 'true' : undefined}
              className={`-ml-px block w-full border-l-2 py-1.5 pl-4 text-left text-[13px] ${
                active
                  ? 'border-primary-500 font-medium text-gray-900 dark:text-white'
                  : 'border-transparent text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              {label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
